import React, { useState, useEffect } from 'react';
import { Palette, Sparkles, Heart, Plus, Trash2, Star } from 'lucide-react';
import { COLOR_MODELS } from '../utils/colorGenerator';
import { 
  addFavoriteModel, 
  removeFavoriteModel, 
  getFavoriteModels, 
  incrementModelUsage,
  addCustomModel,
  getCustomModels,
  removeCustomModel,
  FavoriteModel,
  CustomModel
} from '../utils/database';

interface ModelSelectorProps {
  selectedModel: string;
  onModelChange: (model: string) => void;
  isGenerating: boolean;
  currentPalette: string[];
}

const MODEL_DESCRIPTIONS: Record<string, string> = {
  'default': 'Balanced colors for general use',
  'ui': 'Optimized for user interfaces',
  'makoto_shinkai': 'Inspired by anime aesthetics',
  'metroid_fusion': 'Sci-fi and gaming themes',
  'akira': 'Cyberpunk and neon vibes',
  'flower_photography': 'Natural and organic tones',
  'sunset': 'Warm and golden hues',
  'vintage': 'Retro and muted colors',
  'modern_art': 'Contemporary artistic palettes',
  'cyberpunk': 'Dark futuristic neon themes',
  'forest': 'Natural green woodland tones',
  'ocean': 'Deep blue aquatic colors',
  'desert': 'Warm sandy earth tones',
  'neon': 'Bright electric colors',
  'pastel': 'Soft gentle hues',
  'monochrome': 'Grayscale variations',
  'earth_tones': 'Natural brown and beige',
  'jewel_tones': 'Rich gemstone colors',
  'autumn': 'Fall season warm colors',
  'spring': 'Fresh light seasonal tones',
  'winter': 'Cool crisp seasonal colors',
  'summer': 'Bright warm seasonal hues',
  'corporate': 'Professional business colors',
  'creative': 'Artistic vibrant combinations',
  'minimal': 'Clean simple color schemes',
  'bold': 'Strong high-contrast colors',
  'soft': 'Gentle muted tones',
  'dramatic': 'High-impact dark themes'
};

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  selectedModel,
  onModelChange,
  isGenerating,
  currentPalette
}) => {
  const [favorites, setFavorites] = useState<FavoriteModel[]>([]);
  const [customModels, setCustomModels] = useState<CustomModel[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelDescription, setNewModelDescription] = useState('');

  useEffect(() => {
    loadFavorites();
    loadCustomModels();
  }, []);

  const loadFavorites = () => {
    setFavorites(getFavoriteModels());
  };

  const loadCustomModels = () => {
    setCustomModels(getCustomModels());
  };

  const handleModelSelect = (model: string) => {
    onModelChange(model);
    incrementModelUsage(model);
    loadFavorites(); // Refresh to show updated usage count
  };

  const toggleFavorite = (model: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const isFavorite = favorites.some(fav => fav.name === model);
    
    if (isFavorite) {
      removeFavoriteModel(model);
    } else {
      addFavoriteModel(model, MODEL_DESCRIPTIONS[model] || 'Custom AI model');
    }
    
    loadFavorites();
  };

  const createCustomModel = () => {
    if (newModelName.trim() && newModelDescription.trim() && currentPalette.length > 0) {
      const success = addCustomModel(newModelName.trim(), newModelDescription.trim(), currentPalette);
      if (success) {
        setNewModelName('');
        setNewModelDescription('');
        setShowCreateModal(false);
        loadCustomModels();
      }
    }
  };

  const deleteCustomModel = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    removeCustomModel(id);
    loadCustomModels();
  };

  const isFavorite = (model: string) => favorites.some(fav => fav.name === model);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <Sparkles className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">AI Color Models</h3>
            <p className="text-sm text-gray-600">Choose from {COLOR_MODELS.length} styles trained on different design aesthetics</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center space-x-2 shadow-md text-sm"
          disabled={currentPalette.length === 0}
        >
          <Plus size={14} />
          <span>Create Model</span>
        </button>
      </div>

      {/* Favorite Models */}
      {favorites.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
            <Star className="text-yellow-500 mr-2" size={16} />
            Favorite Models ({favorites.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {favorites.map((favorite) => (
              <div
                key={favorite.name}
                onClick={() => handleModelSelect(favorite.name)}
                className={`p-3 rounded-lg border-2 transition-all duration-200 text-left relative cursor-pointer ${
                  selectedModel === favorite.name
                    ? 'border-yellow-500 bg-yellow-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Star className="text-yellow-500 fill-current" size={12} />
                    <span className={`font-medium capitalize text-xs ${
                      selectedModel === favorite.name ? 'text-yellow-900' : 'text-gray-800'
                    }`}>
                      {favorite.name.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">{favorite.usage_count}x</span>
                </div>
                <p className={`text-xs ${
                  selectedModel === favorite.name ? 'text-yellow-700' : 'text-gray-600'
                }`}>
                  {favorite.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Default Models */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-700 mb-3">
          AI Models ({COLOR_MODELS.length} available)
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {COLOR_MODELS.map((model) => (
            <div
              key={model}
              onClick={() => handleModelSelect(model)}
              className={`p-3 rounded-lg border-2 transition-all duration-200 text-left relative cursor-pointer ${
                selectedModel === model
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div
                onClick={(e) => toggleFavorite(model, e)}
                className={`absolute top-2 right-2 p-1 rounded-full transition-all ${
                  isFavorite(model) 
                    ? 'text-yellow-500 hover:text-yellow-600' 
                    : 'text-gray-400 hover:text-yellow-500'
                }`}
              >
                <Heart className={isFavorite(model) ? 'fill-current' : ''} size={12} />
              </div>
              
              <div className="flex items-center space-x-2 mb-2 pr-6">
                <Palette size={14} className={selectedModel === model ? 'text-blue-600' : 'text-gray-500'} />
                <span className={`font-medium capitalize text-xs ${
                  selectedModel === model ? 'text-blue-900' : 'text-gray-800'
                }`}>
                  {model.replace(/_/g, ' ')}
                </span>
              </div>
              <p className={`text-xs ${
                selectedModel === model ? 'text-blue-700' : 'text-gray-600'
              }`}>
                {MODEL_DESCRIPTIONS[model]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Models */}
      {customModels.length > 0 && (
        <div>
          <h4 className="text-md font-semibold text-gray-700 mb-3 flex items-center">
            <Plus className="text-green-500 mr-2" size={16} />
            Custom Models ({customModels.length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {customModels.map((custom) => (
              <div
                key={custom.name}
                onClick={() => handleModelSelect(custom.name)}
                className={`p-3 rounded-lg border-2 transition-all duration-200 text-left relative cursor-pointer ${
                  selectedModel === custom.name
                    ? 'border-green-500 bg-green-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                } ${isGenerating ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div
                  onClick={(e) => deleteCustomModel(custom.id!, e)}
                  className="absolute top-2 right-2 p-1 rounded-full text-red-400 hover:text-red-600 transition-all"
                >
                  <Trash2 size={12} />
                </div>
                
                <div className="flex items-center space-x-2 mb-2 pr-6">
                  <Plus className="text-green-500" size={12} />
                  <span className={`font-medium text-xs ${
                    selectedModel === custom.name ? 'text-green-900' : 'text-gray-800'
                  }`}>
                    {custom.name}
                  </span>
                </div>
                <p className={`text-xs ${
                  selectedModel === custom.name ? 'text-green-700' : 'text-gray-600'
                }`}>
                  {custom.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Model Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Create Custom Model</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a new AI model based on your current color palette
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model Name</label>
                <input
                  type="text"
                  value={newModelName}
                  onChange={(e) => setNewModelName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Ocean Sunset"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newModelDescription}
                  onChange={(e) => setNewModelDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Describe the style and mood of this color palette..."
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={createCustomModel}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  disabled={!newModelName.trim() || !newModelDescription.trim()}
                >
                  Create Model
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};